import React from 'react';
import { getFormattedAmountValue } from 'common/components/AmountView/utils';
import { AccountingEntryInvoice } from 'ap/signing/accountingEntry/view/__generated__/AccountingEntryInvoice';
import { InvoiceTransactionTypes } from 'common/components/Modules/TransactionEdit/graphql/__generated__/InvoiceTransactionTypes';
import { usePdfRenderer } from 'common/hooks/usePdfRenderer';

interface TableProps {
  data: AccountingEntryInvoice;
  transactionTypeData?: InvoiceTransactionTypes;
}

export const Table: React.FC<TableProps> = ({ data, transactionTypeData }) => {
  const tableData = data.invoice?.invoiceDistributions!;

  const returntaxType = (taxId: number | null) => {
    let taxType = '';
    if (taxId !== null) {
      for (let types of transactionTypeData?.tax1099T4Types?.nodes!) {
        if (taxId.toString() === types.id.toString()) {
          taxType = types?.abbreviation!;
        }
      }
    }

    return taxType;
  };
  const { Text, View, getStyleSheet } = usePdfRenderer();

  const styles = getStyleSheet(() => ({
    headerStyle: {
      fontSize: 8,
      fontWeight: 'bold',
      color: '#605e5c',
    },
    centerHeaderStyle: {
      fontSize: 8,
      fontWeight: 'bold',
      color: '#605e5c',
      alignSelf: 'center' as const,
    },
    normalFont: {
      fontSize: 8,
      color: '#605e5c',
    },
    amountHeaderStyle: {
      fontSize: 8,
      fontWeight: 'bold',
      color: '#605e5c',
      alignSelf: 'center' as const,
    },
    amountFont: {
      fontSize: 8,
      color: '#605e5c',
      alignSelf: 'center' as const,
    },
    centerFont: {
      fontSize: 8,
      color: '#605e5c',
      alignSelf: 'center' as const,
    },
    totalAmountFont: {
      fontSize: 8,
      color: '#0078d4',
      marginTop: 15,
      alignSelf: 'center' as const,
      marginRight: 190,
    },
    tableHeaderWrapper: {
      flexDirection: 'row' as const,
      marginTop: 10,
      marginHorizontal: 20,
      height: 40,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
      borderBottomWidth: 0.2,
    },
    centerHeader: {
      fontSize: 8,
      fontWeight: 'semibold',
      color: '#605e5c',
    },
    flexEndHeader: {
      fontSize: 8,
      color: '#605e5c',
      fontWeight: 'semibold',
      alignSelf: 'center' as const,
    },
    tableRowWrapper: {
      flexDirection: 'row' as const,
      marginHorizontal: 20,
      height: 40,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
    },
    smallFlex: {
      flex: 0.5,
    },
    singleFlex: {
      flex: 1,
    },
    mediumFlex: {
      flex: 1.5,
    },
    largeFlex: {
      flex: 2,
    },
  }));
  if (!tableData.nodes.length || !View || !Text || !styles) {
    return null;
  }

  const headerTableData = [
    {
      headerTitle: 'Account',
      headerStyle: styles.headerStyle,
      wrapperStyle: styles.singleFlex,
    },
    {
      headerTitle: 'Project',
      headerStyle: styles.headerStyle,
      wrapperStyle: styles.singleFlex,
    },
    {
      headerTitle: 'Set',
      headerStyle: styles.headerStyle,
      wrapperStyle: styles.singleFlex,
    },
    {
      headerTitle: 'Item Description',
      headerStyle: styles.headerStyle,
      wrapperStyle: styles.mediumFlex,
    },
    {
      headerTitle: 'Incentive',
      headerStyle: styles.headerStyle,
      wrapperStyle: styles.mediumFlex,
    },
    {
      headerTitle: '1099/T4',
      headerStyle: styles.headerStyle,
      wrapperStyle: styles.singleFlex,
    },
    {
      headerTitle: 'Ins',
      headerStyle: styles.headerStyle,
      wrapperStyle: styles.singleFlex,
    },
    {
      headerTitle: 'Amount',
      headerStyle: styles.amountHeaderStyle,
      wrapperStyle: styles.singleFlex,
    },
    {
      headerTitle: 'FF1',
      headerStyle: styles.centerHeaderStyle,
      wrapperStyle: styles.singleFlex,
    },
    {
      headerTitle: 'FF2',
      headerStyle: styles.centerHeaderStyle,
      wrapperStyle: styles.singleFlex,
    },
    {
      headerTitle: 'FF3',
      headerStyle: styles.centerHeaderStyle,
      wrapperStyle: styles.singleFlex,
    },
    {
      headerTitle: 'FF4',
      headerStyle: styles.centerHeaderStyle,
      wrapperStyle: styles.singleFlex,
    },
  ];

  return (
    <View>
      <View style={{ marginHorizontal: 20, marginTop: 20 }}>
        <Text style={{ fontSize: 12 }}>Accounting</Text>
      </View>
      <View style={styles.tableHeaderWrapper}>
        {headerTableData.map((item, index) => (
          <View style={item.wrapperStyle} key={index}>
            <Text style={item.headerStyle!}>{item.headerTitle}</Text>
          </View>
        ))}
      </View>

      {tableData.nodes.map((item, index) => {
        return (
          <View key={index} style={styles.tableRowWrapper}>
            <View style={[styles.singleFlex]}>
              <Text style={styles.normalFont}>
                {item?.accountReference || '-'}
              </Text>
            </View>
            <View style={[styles.singleFlex]}>
              <Text style={styles.normalFont}>
                {item?.projectReference || '-'}
              </Text>
            </View>
            <View style={[styles.singleFlex]}>
              <Text style={styles.normalFont}>{item?.setReference || '-'}</Text>
            </View>
            <View style={[styles.mediumFlex]}>
              <Text style={styles.normalFont}>
                {item?.additionalDescription || '-'}
              </Text>
            </View>
            <View style={styles.mediumFlex}>
              <Text style={styles.normalFont}>
                {item?.productionIncentive?.incentiveType?.incentiveType || '-'}
              </Text>
            </View>
            <View style={styles.singleFlex}>
              <Text style={styles.normalFont}>
                {item?.tax1099T4TypeId
                  ? returntaxType(item?.tax1099T4TypeId!)
                  : '-'}
              </Text>
            </View>

            <View style={styles.singleFlex}>
              <Text style={styles.normalFont}>
                {item?.referenceCode5 || '-'}
              </Text>
            </View>
            <View style={styles.singleFlex}>
              <Text style={styles.amountFont}>
                {item?.distributionAmount
                  ? getFormattedAmountValue(item?.distributionAmount)
                  : '-'}
              </Text>
            </View>
            <View style={styles.singleFlex}>
              <Text style={styles.centerFont}>
                {item?.referenceCode1 || '-'}
              </Text>
            </View>
            <View style={styles.singleFlex}>
              <Text style={styles.centerFont}>
                {item?.referenceCode2 || '-'}
              </Text>
            </View>
            <View style={styles.singleFlex}>
              <Text style={styles.centerFont}>
                {item?.referenceCode3 || '-'}
              </Text>
            </View>
            <View style={styles.singleFlex}>
              <Text style={styles.centerFont}>
                {item?.referenceCode4 || '-'}
              </Text>
            </View>
          </View>
        );
      })}
      {tableData && (
        <View style={{ borderBottomWidth: 0.1, paddingBottom: 20 }}>
          <Text style={styles.totalAmountFont}>
            {getFormattedAmountValue(
              tableData?.aggregates?.sum?.distributionAmount!.toString() || '-'
            )}
          </Text>
        </View>
      )}
    </View>
  );
};
